// Global
import React from 'react';
import clsx from 'clsx';

// Local
import styles from './HeroDetailCard.module.scss';
import { HeroProps } from '../Hero';
import useDictionary from 'lib/hooks/useDictionary';
import { formatDateYMD } from 'lib/utils/date-formatter';
import { useCtaComponentClass } from 'lib/hooks/theming/useCtaComponentClass';
export type HeroDetailCardProps = HeroProps & {
  contentAlignment?: 'left' | 'center';
  showByLine?: boolean;
};

export const HeroDetailCard = (props: HeroDetailCardProps) => {
  const contentAlignment = props?.contentAlignment || 'left';
  const heroCtaColor = useCtaComponentClass('component-carousel-pagination-color');
  // Dictionary values
  const { getDictionaryValue } = useDictionary();
  const byText = getDictionaryValue('Common.ByLine.By', 'By');
  const publishedText = getDictionaryValue('Common.ByLine.Published');
  const seoModifiedText = getDictionaryValue('Common.ByLine.Modified');

  const hasEyebrow = !!props?.contentEyebrow;
  // Pre-calculate all conditions and values
  const hasAuthorName = !!props?.authorName;
  const hasAuthorProfileURL = !!props.authorProfileURL;
  const DisplayDate = props.DisplayDate;
  const SEOModifiedDate = props.SEOModifiedDate;
  // DisplayDate and SEOModifiedDate logic
  const publishDateString = typeof DisplayDate === 'string' ? DisplayDate : undefined;

  const seoModifiedDateString = typeof SEOModifiedDate === 'string' ? SEOModifiedDate : undefined;

  const publishDateKey = formatDateYMD(publishDateString);
  const seoModifiedDateKey = formatDateYMD(seoModifiedDateString);

  let finalDateLabel: string | null = null;
  let finalDateValue: string | null = null;
  // Rule 1
  if (!publishDateString && seoModifiedDateString) {
    finalDateLabel = seoModifiedText;
    finalDateValue = seoModifiedDateString;
  }
  // Rule 2
  else if (publishDateString && !seoModifiedDateString) {
    finalDateLabel = publishedText;
    finalDateValue = publishDateString;
  }
  // Rule 3
  else if (publishDateString && seoModifiedDateString && publishDateKey && seoModifiedDateKey) {
    if (seoModifiedDateKey > publishDateKey) {
      finalDateLabel = seoModifiedText;
      finalDateValue = seoModifiedDateString;
    } else {
      finalDateLabel = publishedText;
      finalDateValue = publishDateString;
    }
  }
  const authorProfileHref = props.authorProfileURL;

  const hasHeading = !!props?.contentHeadline;
  const hasContentDescription = !!props?.contentDescription;

  const hasCtas = !!props?.cta1 || !!props?.cta2;

  return (
    <div
      className={clsx(
        styles.contentContainer,
        styles[`contentAlignment--${contentAlignment}`],
        styles[`hero${props.heroVariant}`]
      )}
    >
      {hasEyebrow && (
        <div className={styles.titleWrapper}>
          {hasEyebrow && <span className={styles.heroEyebrowText}>{props?.contentEyebrow}</span>}
        </div>
      )}

      {hasHeading && (
        <div className={styles.heroHeadingWrapper}>
          <h1 className={styles.heroHeadingText}>{props?.contentHeadline}</h1>
        </div>
      )}

      {hasAuthorName && (
        <div className={styles.byLine}>
          <div className={styles.byAuthorWrapper}>
            {!props.hideAuthorBySignaturePrefix && (
              <>
                <span className={styles.byLabel}>{byText}</span>{' '}
              </>
            )}
            {hasAuthorProfileURL ? (
              <a className={styles.authorLink} href={authorProfileHref}>
                <span className={styles.authorName}>{props.authorName}</span>
              </a>
            ) : (
              <span className={styles.authorName}>{props.authorName}</span>
            )}
          </div>

          {finalDateLabel && finalDateValue && (
            <>
              <span className={styles.separator} aria-hidden="true">
                |{' '}
              </span>
              <span className={styles.publicationDate}>
                {finalDateLabel}{' '}
                <time dateTime={formatDateYMD(finalDateValue)}>{finalDateValue}</time>
              </span>
            </>
          )}
        </div>
      )}

      {hasContentDescription && (
        <div className={styles.heroDescriptionText}>{props?.contentDescription}</div>
      )}

      {hasCtas && (
        <div className={clsx(styles.heroCtaContainer, styles[heroCtaColor])}>
          {props.cta1}
          {props.cta2}
        </div>
      )}
    </div>
  );
};
